import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../api/client';

export interface UserProfile {
  name: string;
  email: string;
  phone: string;
  planName: string; // river display name from the real billing tier, e.g. "Free" / "Ganga" / "Yamuna"
  planKey: string;  // resolved backend tier, e.g. "starter" / "pro" / "business"
}

const ls = {
  get: (key: string, fallback?: any) => {
    try {
      const v = localStorage.getItem(key);
      return v ? JSON.parse(v) : fallback;
    } catch {
      return fallback;
    }
  },
  set: (key: string, value: any) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* noop */
    }
  },
};

// Cache key for the last-known plan, so the sidebar doesn't flash "Free" for
// a returning user while the real GET /billing/me is still in flight.
const PLAN_CACHE_KEY = 'miraal_billing_cache';

/**
 * Unified user display data: backend auth user + local profile overrides
 * (name/email/phone tweaks the user makes in Settings before they're synced)
 * + the REAL billing plan from GET /api/v1/billing/me - the same endpoint
 * Settings -> Billing uses. Previously planName/planKey were hardcoded to
 * "Yamuna", so every user's sidebar showed a plan they might not be on.
 */
export const useUserProfile = (): UserProfile & { updateProfile: (p: Partial<UserProfile>) => void } => {
  const { user, isAuthenticated } = useAuth();

  const stored = ls.get('miraal_profile', {});
  const [plan, setPlan] = useState<{ river: string; plan: string } | null>(() => ls.get(PLAN_CACHE_KEY, null));

  const loadPlan = useCallback(() => {
    if (!isAuthenticated) return;
    apiClient.billing
      .getMe()
      .then((b) => {
        const next = { river: b.river, plan: b.plan };
        setPlan(next);
        ls.set(PLAN_CACHE_KEY, next);
      })
      .catch(() => {
        /* keep whatever was last cached (or the Free default below) rather
           than throwing - this hook is read all over the chat UI. */
      });
  }, [isAuthenticated]);

  useEffect(() => {
    loadPlan();
    // Fired by PricingOverlay after POST /billing/plan succeeds, and by
    // Settings -> Billing after a change - keeps every consumer in sync.
    window.addEventListener('miraal-plan-changed', loadPlan);
    return () => window.removeEventListener('miraal-plan-changed', loadPlan);
  }, [loadPlan]);

  const profile = useMemo(() => {
    const name = stored.name || user?.firstName || 'User';
    const email = stored.email || user?.email || 'user@miraal.ai';
    const phone = stored.phone || user?.mobile || '';
    const planName = plan?.river || 'Free';
    const planKey = plan?.plan || 'starter';
    return { name, email, phone, planName, planKey };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stored.name, stored.email, stored.phone, user?.firstName, user?.email, user?.mobile, plan]);

  const updateProfile = useCallback((updates: Partial<UserProfile>) => {
    // Plan is real backend data now (see loadPlan above) - local overrides
    // only apply to display fields, not planName/planKey.
    const { planName: _planName, planKey: _planKey, ...displayUpdates } = updates;
    const current = ls.get('miraal_profile', {});
    ls.set('miraal_profile', { ...current, ...displayUpdates });
    window.dispatchEvent(new Event('miraal-profile-updated'));
  }, []);

  return { ...profile, updateProfile };
};
